import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { Spinner } from './ui';
import MelioBrandMark from './brand/MelioBrandMark';

const SITE_ACCESS_TOKEN_KEY = 'melio_site_access_token';

type SiteAccessResponse = {
  valid: boolean;
  token?: string;
  expiresInMs?: number;
};

export default function SiteAccessGate({ children }: { children: React.ReactNode }) {
  const [password, setPassword] = useState('');
  const [accessGranted, setAccessGranted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifySiteAccess = httpsCallable<{ token: string }, SiteAccessResponse>(functions, 'verifySiteAccess');
    const savedToken = localStorage.getItem(SITE_ACCESS_TOKEN_KEY);

    if (!savedToken) {
      setLoading(false);
      return;
    }

    verifySiteAccess({ token: savedToken })
      .then((result) => {
        if (result.data.valid) {
          setAccessGranted(true);
        } else {
          localStorage.removeItem(SITE_ACCESS_TOKEN_KEY);
        }
      })
      .catch(() => {
        localStorage.removeItem(SITE_ACCESS_TOKEN_KEY);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const verifySiteAccess = httpsCallable<{ password: string }, SiteAccessResponse>(functions, 'verifySiteAccess');
      const result = await verifySiteAccess({ password });
      if (!result.data.valid || !result.data.token) {
        setError('Password incorrect. Please try again.');
        return;
      }

      localStorage.setItem(SITE_ACCESS_TOKEN_KEY, result.data.token);
      setAccessGranted(true);
      setPassword('');
    } catch (err) {
      setError('Password incorrect. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center gh-surface"
        style={{ backgroundColor: 'var(--gh-oxblood)' }}
      >
        <Spinner size="lg" />
      </div>
    );
  }

  if (accessGranted) {
    return <>{children}</>;
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 gh-surface"
      style={{ backgroundColor: 'var(--gh-oxblood)' }}
    >
      <div
        className="w-full max-w-md p-8 md:p-10 border rounded-2xl"
        style={{
          backgroundColor: 'var(--gh-bone)',
          borderColor: 'var(--gh-rule)',
          boxShadow: '0 32px 70px rgba(61, 11, 13, 0.28)',
        }}
      >
        <div className="flex flex-col items-center mb-6">
          <MelioBrandMark variant="icon" height={56} />
          <MelioBrandMark variant="wordmark" height={22} className="mt-4" />
        </div>
        <p className="gh-eyebrow text-center mb-2">Restricted access</p>
        <p className="text-sm text-center mb-6" style={{ color: 'var(--gh-ash)' }}>
          Enter your access code to continue
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="site-access-password" className="block text-sm font-medium mb-1.5" style={{ color: 'var(--gh-graphite)' }}>
              Access code
            </label>
            <input
              id="site-access-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 border rounded-lg"
              style={{
                borderColor: 'var(--gh-rule)',
                backgroundColor: 'var(--gh-bone-soft)',
                color: 'var(--gh-graphite)',
              }}
            />
          </div>

          {error && (
            <p className="text-sm" style={{ color: '#8f2d2d' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="gh-btn-primary w-full disabled:opacity-50"
          >
            {submitting ? 'Checking…' : 'Enter site'}
          </button>
        </form>
      </div>
    </div>
  );
}
